import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Lock, User, Check } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import FormInput from './FormInput';
import SocialAuthButtons from './SocialAuthButtons';
import PasswordStrength from './PasswordStrength';
import { registerSchema } from '../../schemas/authSchemas';
import { useAuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { register: registerUser, isLoading } = useAuthContext();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: 'onChange'
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const onSubmit = async (data) => {
    const result = await registerUser(data);
    
    if (result.success) {
      toast.success('¡Cuenta creada! Revisa tu email', {
        icon: '✅',
        duration: 4000
      });
      onClose();
    } else {
      toast.error(result.error || 'Error al crear la cuenta', {
        icon: '❌',
        duration: 3000
      });
    }
  };

  const handleSocialLogin = (provider) => {
    toast.success(`Registrándose con ${provider}...`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Crear cuenta</h2>
            <p className="text-muted-foreground">Únete a la comunidad de La Orda</p>
          </div>

          {/* Social Login */}
          <SocialAuthButtons
            onGoogleLogin={() => handleSocialLogin('Google')}
            onFacebookLogin={() => handleSocialLogin('Facebook')}
          />

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-muted-foreground text-sm">O regístrate con email</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Nombre"
                name="nombre"
                type="text"
                placeholder="Juan"
                icon={<User className="w-5 h-5" />}
                error={errors.nombre?.message}
                required
                register={register}
              />

              <FormInput
                label="Apellido"
                name="apellido"
                type="text"
                placeholder="Pérez"
                error={errors.apellido?.message}
                required
                register={register}
              />
            </div>

            <FormInput
              label="Email"
              name="email"
              type="email"
              placeholder="tu@email.com"
              icon={<Mail className="w-5 h-5" />}
              error={errors.email?.message}
              required
              register={register}
            />

            <div>
              <FormInput
                label="Contraseña"
                name="password"
                type="password"
                placeholder="••••••••"
                icon={<Lock className="w-5 h-5" />}
                error={errors.password?.message}
                required
                register={register}
              />
              <PasswordStrength password={password} />
            </div>

            <FormInput
              label="Confirmar contraseña"
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              icon={<Lock className="w-5 h-5" />}
              rightIcon={
                passwordsMatch ? (
                  <Check className="w-5 h-5 text-success" />
                ) : null
              }
              error={errors.confirmPassword?.message}
              required
              register={register}
            />

            <div className="flex items-start gap-2">
              <Checkbox id="terms" {...register('acceptTerms')} className="mt-1" />
              <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                Acepto los{' '}
                <a href="#" className="text-primary hover:underline">
                  términos y condiciones
                </a>{' '}
                y la{' '}
                <a href="#" className="text-primary hover:underline">
                  política de privacidad
                </a>
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-destructive text-sm">{errors.acceptTerms.message}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading || !isValid}
            >
              {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            ¿Ya tienes cuenta?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-primary hover:underline font-medium"
            >
              Inicia sesión
            </button>
          </p>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterModal;